// Constants
export const DEFAULT_LANGUAGE = 'en';
export const DEFAULT_CATEGORY = 'UTILITY';
export const COMPONENT_TYPES = {
  HEADER: 'HEADER',
  BODY: 'BODY',
  FOOTER: 'FOOTER',
  BUTTONS: 'BUTTONS',
};
export const MEDIA_FORMATS = ['IMAGE', 'VIDEO', 'DOCUMENT'];

// WhatsApp button type → lucide icon (display only). Unknown types render
// label-only.
export const BUTTON_TYPE_ICON = {
  QUICK_REPLY: 'i-lucide-reply',
  URL: 'i-lucide-external-link',
  PHONE_NUMBER: 'i-lucide-phone',
  COPY_CODE: 'i-lucide-copy',
};

export const findComponentByType = (template, type) =>
  template.components?.find(component => component.type === type);

export const processVariable = str => {
  return str.replace(/{{|}}/g, '');
};

export const allKeysRequired = value => {
  const keys = Object.keys(value);
  return keys.every(key => value[key]);
};

export const replaceTemplateVariables = (templateText, processedParams) => {
  return templateText.replace(/{{([^}]+)}}/g, (match, variable) => {
    const variableKey = processVariable(variable);
    return processedParams.body?.[variableKey] || `{{${variable}}}`;
  });
};

// Find a template DEFINITION on an inbox by name (+ language when possible).
// Mirrors the dual-source lookup in store/modules/inboxes.js. Returns null when
// the template is no longer synced to the inbox (deleted / renamed).
export const findTemplateInInbox = (inbox, name, language) => {
  const templates =
    inbox?.message_templates || inbox?.additional_attributes?.message_templates;
  if (!Array.isArray(templates) || !name) return null;

  return (
    templates.find(t => t.name === name && t.language === language) ||
    templates.find(t => t.name === name) ||
    null
  );
};

// Combine a template definition (labels/footer/header structure) with the
// filled values + rendered body into a single view-model the shared
// TemplatePreview renders. Reads media keys defensively because the timeline
// path is camelCased (mediaUrl) while the send form keeps snake_case
// (media_url).
export const buildTemplateViewModel = ({
  definition,
  processedParams = {},
  renderedBody = '',
  name = '',
} = {}) => {
  if (!definition) {
    return {
      name,
      header: null,
      body: renderedBody,
      footer: null,
      buttons: [],
      isPartial: true,
    };
  }

  const params = processedParams || {};
  const headerComponent = findComponentByType(
    definition,
    COMPONENT_TYPES.HEADER
  );
  const footerComponent = findComponentByType(
    definition,
    COMPONENT_TYPES.FOOTER
  );
  const buttonsComponent = findComponentByType(
    definition,
    COMPONENT_TYPES.BUTTONS
  );

  let header = null;
  if (headerComponent) {
    if (MEDIA_FORMATS.includes(headerComponent.format)) {
      const h = params.header || {};
      header = {
        kind: 'media',
        mediaType: headerComponent.format.toLowerCase(),
        url: h.media_url ?? h.mediaUrl ?? null,
        name: h.media_name ?? h.mediaName ?? null,
      };
    } else if (headerComponent.text) {
      header = { kind: 'text', text: headerComponent.text };
    }
  }

  const buttons = (buttonsComponent?.buttons || []).map(button => ({
    label: button.text,
    type: button.type,
    icon: BUTTON_TYPE_ICON[button.type] || null,
  }));

  return {
    name,
    header,
    body: renderedBody,
    footer: footerComponent?.text || null,
    buttons,
    isPartial: false,
  };
};

export const buildTemplateParameters = (template, hasMediaHeaderValue) => {
  const allVariables = {};

  const bodyComponent = findComponentByType(template, COMPONENT_TYPES.BODY);
  const headerComponent = findComponentByType(template, COMPONENT_TYPES.HEADER);

  if (!bodyComponent) return allVariables;

  const templateString = bodyComponent.text;

  // Process body variables
  const matchedVariables = templateString.match(/{{([^}]+)}}/g);
  if (matchedVariables) {
    allVariables.body = {};
    matchedVariables.forEach(variable => {
      const key = processVariable(variable);
      allVariables.body[key] = '';
    });
  }

  if (hasMediaHeaderValue) {
    if (!allVariables.header) allVariables.header = {};
    allVariables.header.media_url = '';
    allVariables.header.media_type = headerComponent.format.toLowerCase();

    // For document templates, include media_name field for filename support
    if (headerComponent.format.toLowerCase() === 'document') {
      allVariables.header.media_name = '';
    }
  }

  // Process button variables
  const buttonComponents = template.components.filter(
    component => component.type === COMPONENT_TYPES.BUTTONS
  );

  buttonComponents.forEach(buttonComponent => {
    if (buttonComponent.buttons) {
      buttonComponent.buttons.forEach((button, index) => {
        // Handle URL buttons with variables
        if (button.type === 'URL' && button.url && button.url.includes('{{')) {
          const buttonVars = button.url.match(/{{([^}]+)}}/g) || [];
          if (buttonVars.length > 0) {
            if (!allVariables.buttons) allVariables.buttons = [];
            allVariables.buttons[index] = {
              type: 'url',
              parameter: '',
              url: button.url,
              variables: buttonVars.map(v => processVariable(v)),
            };
          }
        }

        // Handle copy code buttons
        if (button.type === 'COPY_CODE') {
          if (!allVariables.buttons) allVariables.buttons = [];
          allVariables.buttons[index] = {
            type: 'copy_code',
            parameter: '',
          };
        }
      });
    }
  });

  return allVariables;
};
